import { CHART_DISPLAY_NAME_MAX_CHARS } from "../constants/chartUploadLimits";
import { ChartDocument } from "../types/chart";

/**
 * Legacy metadata field that older saves used to store the stringified
 * ChartDocument. New saves embed the JSON inside the PNG body via a tEXt
 * chunk instead (see buildChartMarkupPngBlob), which avoids stuffing
 * potentially-large payloads into upload metadata fields whose size limits
 * vary by backend storage. Reads still consult this key for backward
 * compatibility with existing saves.
 */
export const CHART_MARKUP_METADATA_KEY = "chartDocument";

/** Some list/detail payloads use snake_case for the same blob. */
const CHART_MARKUP_METADATA_KEY_SNAKE = "chart_document";

/**
 * 1×1 PNG placeholder — SSG document upload only allows image/pdf extensions.
 * Used as the canonical container that we splice tEXt chunks into.
 */
const PLACEHOLDER_PNG_BASE64 =
  "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function decodePlaceholderPng(): Uint8Array {
  const binary = atob(PLACEHOLDER_PNG_BASE64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function chartMarkupPlaceholderPngBlob(): Blob {
  return new Blob([decodePlaceholderPng() as BlobPart], { type: "image/png" });
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function utf8ToBase64(input: string): string {
  const bytes = new TextEncoder().encode(input);
  // Avoid spreading large arrays (stack overflow on huge strings).
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUtf8(input: string): string {
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

/**
 * Build a PNG `tEXt` chunk: 4-byte length, "tEXt" type, keyword (Latin-1) +
 * NUL separator + text (Latin-1), 4-byte CRC32 over type+data.
 */
function buildTextChunk(keyword: string, text: string): Uint8Array {
  const keywordBytes = new Uint8Array(keyword.length);
  for (let i = 0; i < keyword.length; i += 1) {
    keywordBytes[i] = keyword.charCodeAt(i) & 0xff;
  }
  const textBytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) {
    textBytes[i] = text.charCodeAt(i) & 0xff;
  }
  const dataLength = keywordBytes.length + 1 + textBytes.length;
  const chunk = new Uint8Array(4 + 4 + dataLength + 4);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, dataLength, false);
  // "tEXt"
  chunk[4] = 0x74;
  chunk[5] = 0x45;
  chunk[6] = 0x58;
  chunk[7] = 0x74;
  chunk.set(keywordBytes, 8);
  chunk[8 + keywordBytes.length] = 0x00;
  chunk.set(textBytes, 8 + keywordBytes.length + 1);
  const crc = crc32(chunk.subarray(4, 8 + dataLength));
  view.setUint32(8 + dataLength, crc, false);
  return chunk;
}

/** Locate a top-level chunk by 4-char type. Returns offset of length field, or -1. */
function findChunkOffset(bytes: Uint8Array, type: string): number {
  if (bytes.length < 8) return -1;
  for (let i = 0; i < 8; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return -1;
  }
  let offset = 8;
  while (offset + 8 <= bytes.length) {
    const length =
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3];
    const t = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7],
    );
    if (t === type) return offset;
    offset += 8 + length + 4;
  }
  return -1;
}

/**
 * Encode the ChartDocument as a real 1×1 PNG with a `tEXt` chunk carrying the
 * base64-encoded JSON. The image still passes any backend image validation,
 * but the payload now lives in the document body — no more risk of metadata
 * field truncation when a chart has many strokes.
 */
export function buildChartMarkupPngBlob(doc: ChartDocument): Blob {
  const base = decodePlaceholderPng();
  const idatOffset = findChunkOffset(base, "IDAT");
  if (idatOffset < 0) {
    // Should never happen — placeholder is a known good 1×1 PNG.
    return new Blob([base as BlobPart], { type: "image/png" });
  }
  const json = JSON.stringify(doc);
  const text = utf8ToBase64(json);
  const textChunk = buildTextChunk(CHART_MARKUP_METADATA_KEY, text);
  const result = new Uint8Array(base.length + textChunk.length);
  result.set(base.subarray(0, idatOffset), 0);
  result.set(textChunk, idatOffset);
  result.set(base.subarray(idatOffset), idatOffset + textChunk.length);
  return new Blob([result as BlobPart], { type: "image/png" });
}

/**
 * Walk PNG chunks looking for a `tEXt` whose keyword is CHART_MARKUP_METADATA_KEY,
 * then base64-decode + JSON.parse its text. Returns null if no embedded chart
 * payload is present or parsing fails.
 */
export function parseChartDocumentFromPngBytes(
  bytes: Uint8Array,
): ChartDocument | null {
  if (bytes.length < 8) return null;
  for (let i = 0; i < 8; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return null;
  }
  let offset = 8;
  while (offset + 8 <= bytes.length) {
    const length =
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3];
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    if (dataEnd + 4 > bytes.length) return null;
    const t = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7],
    );
    if (t === "tEXt") {
      let sep = dataStart;
      while (sep < dataEnd && bytes[sep] !== 0) sep += 1;
      let keyword = "";
      for (let i = dataStart; i < sep; i += 1) {
        keyword += String.fromCharCode(bytes[i]);
      }
      if (
        keyword === CHART_MARKUP_METADATA_KEY ||
        keyword === CHART_MARKUP_METADATA_KEY_SNAKE
      ) {
        let text = "";
        for (let i = sep + 1; i < dataEnd; i += 1) {
          text += String.fromCharCode(bytes[i]);
        }
        try {
          return JSON.parse(base64ToUtf8(text)) as ChartDocument;
        } catch {
          return null;
        }
      }
    }
    if (t === "IEND") return null;
    offset = dataEnd + 4;
  }
  return null;
}

export function buildChartMarkupUploadMetadata(
  docWithMeta: ChartDocument,
  clientId: string,
): Record<string, unknown> {
  const title = docWithMeta.metadata?.title
    ?.trim()
    .slice(0, CHART_DISPLAY_NAME_MAX_CHARS);
  return {
    type: "chart_markup",
    clientId,
    /** Duplicated so list APIs that omit nested JSON can still show a name. */
    ...(title ? { title } : {}),
  };
}

export function parseChartDocumentFromVersionMetadata(
  metadata: Record<string, unknown> | null | undefined,
): ChartDocument | null {
  if (!metadata) return null;
  const raw =
    metadata[CHART_MARKUP_METADATA_KEY] ??
    metadata[CHART_MARKUP_METADATA_KEY_SNAKE];
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw) as ChartDocument;
  } catch {
    return null;
  }
}
